import {
  calculatePlaybackRate,
  createGraph,
  EMPTY_PATH,
  float32ArrayToAudioBuffer,
  getClipVFSPath,
  not,
  SAMPLING_RATE
} from '@arpeggi/audio'
import { StemType, TransportStates } from '@arpeggi/shared'
import { el } from '@elemaudio/core'

import WebAudioRenderer from '@elemaudio/web-renderer'
import debounce from 'lodash.debounce'
import { getSnapshot, getType } from 'mobx-state-tree'
import { WaveFile } from 'wavefile'
import { Arp2Utils } from '../../client/SongUtils'
import { IClipStore } from '../../stores/song/clip/ClipStore'
import { IDrumStemStore } from '../../stores/song/stem/DrumStemStore'
import { IInstrumentStemStore } from '../../stores/song/stem/InstrumentStemStore'
import { IStudioStore } from '../../stores/StudioStore'
// @ts-ignore
import ClipWorker from './Clip.worker'
import { createSampleMap, prepareSample } from './EngineSampleUtils'

// const MINUS_6DB = 0.50118

type RendererType = 'core' | 'offline'

// Overall Arpeggi elementary engine documentation
// https://drive.google.com/file/d/1Ury0osx4mErVCk-ElMivlyrT23dqCG93/view?usp=sharing
export class AudioEngine {
  private audioContext: AudioContext
  private core: WebAudioRenderer
  private offlineRenderer: any

  public store: any | IStudioStore

  public isBooted: boolean = false

  private vfsPaths = {
    core: new Set(),
    offline: new Set()
  }

  // stores clip paths for clip transformations that're currently being processed
  private inProgressClipTransforms = new Set<string>()

  constructor() {
    this.core = new WebAudioRenderer()
  }

  async boot(audioContext: AudioContext, store: any) {
    this.audioContext = audioContext
    this.store = store

    const virtualFileSystem = {}

    let node = await this.core.initialize(this.audioContext, {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [2],
      processorOptions: {
        virtualFileSystem
      }
    })

    node.connect(audioContext.destination)

    this.registerEventListeners()

    // saves a dummy buffer to use while clip transforms are calculated
    this.updateVirtualFileSystem(
      'core',
      EMPTY_PATH,
      float32ArrayToAudioBuffer([new Float32Array([0]), new Float32Array([0])])
    )
  }

  updateVirtualFileSystem(renderer: string, path: string, audioData: AudioBuffer) {
    const secondaryChannel = audioData.numberOfChannels > 1 ? 1 : 0
    if (renderer === 'offline') {
      this.offlineRenderer.updateVirtualFileSystem({ [`${path}:0`]: audioData.getChannelData(0) })
      this.offlineRenderer.updateVirtualFileSystem({
        [`${path}:1`]: audioData.getChannelData(secondaryChannel)
      })
    } else {
      this.core.updateVirtualFileSystem({ [`${path}:0`]: audioData.getChannelData(0) })
      this.core.updateVirtualFileSystem({
        [`${path}:1`]: audioData.getChannelData(secondaryChannel)
      })
    }
    if (audioData.duration !== 0) this.vfsPaths[renderer].add(path)
  }

  vfsContainsPath(pathName: string, renderer: RendererType): boolean {
    return this.vfsPaths[renderer]?.has(pathName)
  }

  unlockAudioContext() {
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume()
    }
  }

  registerEventListeners() {
    const self = this
    // Booted
    this.core.on('load', () => {
      this.core.on('snapshot', (s: any) => {
        if (s.source === 'playhead' && self.store.transportState === TransportStates.PLAYING) {
          self.store.setPlayheadElapsedTicks(s.data)
        }
      })

      this.core.on('error', function (e) {
        console.log('Core Error', e)
      })

      self.isBooted = true
    })
  }

  reset() {
    this.core.reset()
  }

  metronome(transport, ppqnSignal, elapsedTicks) {
    const metroTrain = el.le(el.div(el.mod(el.add(ppqnSignal, elapsedTicks), 48 * 2), 48 * 2), 0.15)
    const downbeatTrain = el.le(
      el.div(el.mod(el.add(ppqnSignal, elapsedTicks), 48 * 8), 48 * 8),
      0.15
    )
    const metroEnv = el.smooth(el.tau2pole(0.0001), el.mul(transport, metroTrain))
    const semitoneC5 = calculatePlaybackRate(Arp2Utils.getMiddleC() + 12)
    const semitoneF4 = calculatePlaybackRate(Arp2Utils.getMiddleC() + 5)
    const metroFreq = el.select(downbeatTrain, semitoneC5, semitoneF4)
    return el.sample(
      { key: 'metro-sample', path: '29:0', mode: 'trigger', startOffset: 66 },
      metroEnv,
      metroFreq
    )
  }

  debouncedRender = debounce(() => this.render(), 100)

  render() {
    if (!this.isBooted || !this.store.updateAudio || !this.store.allSamplesLoaded()) return
    this.unlockAudioContext()
    try {
      // Clock and transport
      const timelineEngaged = el.const({ key: 'tle', value: this.store.timelineEngaged ? 1 : 0 })
      const transport = el.and(
        el.const({
          key: 'transport',
          value: this.store.transportState === TransportStates.PLAYING ? 1 : 0
        }),
        not(timelineEngaged)
      )
      const graphProps = {
        stems: this.store.song.stems,
        transport,
        soloTrackIndex: this.store.soloTrackIndex,
        repeat: {
          repeatSection: this.store.repeatSection,
          repeatStart: this.store.repeatStart,
          repeatEnd: this.store.repeatEnd
        },
        playheadStartPositionTicks: this.store.playheadStartPositionTicks
      }
      const graph = this.createOnlineGraph(graphProps)
      console.log('[Updated Audio Graph]: ', this.core.render(graph[0], graph[1]))
    } catch (e) {
      console.error('[Audio Error]: ', e)
    }
  }

  // return true if a clip path is currently being processed
  clipIsProcessing(clip: IClipStore): boolean {
    const path = getClipVFSPath(clip, clip.getAudioSourceDurationInSeconds(), this.store.song.tempo)
    return this.inProgressClipTransforms.has(path)
  }

  // runs a worker that will generate the clip's transformed audio buffer, then saves it to the VFS
  async addClipToVFSAsync({
    clip,
    audioBuffer,
    tempo,
    mode
  }: {
    clip: IClipStore
    audioBuffer: AudioBuffer
    tempo: number
    mode: string
  }) {
    const clipWorker = ClipWorker()
    // processes the clip in the worker
    clipWorker.postMessage({
      clip: getSnapshot(clip),
      audioBuffer: [
        audioBuffer.getChannelData(0),
        audioBuffer.getChannelData(audioBuffer.numberOfChannels > 1 ? 1 : 0)
      ],
      audioDuration: audioBuffer.duration,
      tempo,
      mode
    })
    // saves the processed clip in the VFS
    clipWorker.onmessage = (message) => {
      clipWorker.terminate()
      const { mode, path, ab } = message.data
      this.updateVirtualFileSystem(mode, path, float32ArrayToAudioBuffer(ab))
      console.log('[Setting Clip]', path)
      this.store.removeProcessingClip(clip.uniqueId)
      this.inProgressClipTransforms.delete(path)
      this.debouncedRender()
    }
  }

  /**
   * This takes the MobX Stores and passes them into the portable createGraph() function.
   * Online here means this is running in a live AudioEngine instance.
   */
  createOnlineGraph({
    stems,
    transport,
    soloTrackIndex = -1,
    repeat,
    playheadStartPositionTicks,
    mode = 'core'
  }) {
    const sampleMaps = stems.map((stem) => {
      switch (stem.type()) {
        case StemType.INSTRUMENT:
          return prepareSample((stem as IInstrumentStemStore).melodicSample, stem).sampleMap
        case StemType.DRUMS:
          return createSampleMap((stem as IDrumStemStore).drumSamples, this)
        case StemType.AUDIO:
          return stem.clips.map((clip: IClipStore, i: number) => {
            const { sampleMap, audioBuffer } = prepareSample(clip.sampleWithEffects, stem)
            const path = getClipVFSPath(clip, sampleMap.audioDuration, this.store.song.tempo)
            if (
              !this.vfsContainsPath(path, mode as RendererType) &&
              !this.inProgressClipTransforms.has(path)
            ) {
              this.store.addProcessingClip(clip.uniqueId)
              this.inProgressClipTransforms.add(path)
              this.addClipToVFSAsync({ clip, audioBuffer, mode, tempo: this.store.song.tempo })
            }
            return {
              ...clip,
              sampleMap,
              key: i,
              isProcessing: this.inProgressClipTransforms.has(path)
            }
          })
      }
    })
    return createGraph(
      stems,
      transport,
      this.store.song.tempo,
      this.store.transportState === TransportStates.PLAYING ? this.store.currentGain : 0,
      repeat,
      sampleMaps,
      playheadStartPositionTicks,
      soloTrackIndex,
      mode,
      getType(this.store).name,
      { value: this.store.metronome, metronome: this.metronome }
    )
  }

  /** TODO: Deprecate this in favor of offlineRenderService. */
  createOfflineGraph(stems) {
    // Enable transport with default values for offline renderer
    const timelineEngaged = el.const({ key: 'tle', value: 0 })
    const transport = el.and(
      el.const({
        key: 'transport',
        value: 1
      }),
      not(timelineEngaged)
    )
    const graphProps = {
      stems: stems,
      transport,
      repeat: {
        repeatSection: false,
        repeatStart: 0,
        repeatEnd: 16
      },
      playheadStartPositionTicks: 0,
      mode: 'offline'
    }
    return this.createOnlineGraph(graphProps)
  }

  renderOffline({ graph, stereo = true }) {
    // Process the audio. Afterwards, `outs` contains the raw
    // audio data resulting in the offline process.
    const samplingRate = SAMPLING_RATE
    const sampleCount = samplingRate * this.store.song.lengthInSeconds
    let outputs = [new Float32Array(sampleCount)]
    // Render the studio graph with offline renderer
    if (stereo) {
      this.offlineRenderer.render(graph[0], graph[1])
      outputs = [new Float32Array(sampleCount), new Float32Array(sampleCount)]
    } else {
      this.offlineRenderer.render(graph[0])
    }

    this.offlineRenderer.process([], outputs)
    // Create a stereo/mono wave file, 44.1 kHz, 32-bit
    const wav = new WaveFile()
    wav.fromScratch(stereo ? 2 : 1, samplingRate, '32f', outputs)
    return wav
  }
}
